import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { GoogleMap } from '@react-google-maps/api';
import { useGoogleMaps } from '../../providers/GoogleMapsProvider';

const AU_CENTER   = { lat: -25.2744, lng: 133.7751 };
const MAP_STYLE   = { width: '100%', height: '100%' };
const MAP_OPTIONS = {
  mapId: 'austnews-crm-map',
  zoomControl: true,
  fullscreenControl: true,
};

// Pin colours
const COLOR_SCHOOL    = '#E65100'; // orange — school only
const COLOR_BUSINESS  = '#7C3AED'; // purple — business only
const COLOR_BOTH      = '#1565C0'; // blue   — school + business

function buildInfoWindowContent(org) {
  const isSchool   = org.isSchool   === 1 || org.isSchool   === true;
  const isBusiness = org.isBusiness === 1 || org.isBusiness === true;
  const typeLabel  = isSchool && isBusiness ? 'School & Business'
                   : isSchool ? 'School' : 'Business';
  const lines = [
    `<div style="font-family:sans-serif;font-size:13px;max-width:220px;line-height:1.5">`,
    `<strong style="font-size:14px">${org.name}</strong>`,
    `<div style="color:#666;margin-top:2px">${typeLabel}</div>`,
    org.suburb ? `<div>${org.suburb}${org.state ? ', ' + org.state : ''}</div>` : '',
    org.primaryEmail ? `<div>${org.primaryEmail}</div>` : '',
    org.primaryPhone ? `<div>${org.primaryPhone}</div>` : '',
    `<div style="color:#1976d2;margin-top:4px;font-size:12px">Click to view details</div>`,
    `</div>`,
  ];
  return lines.join('');
}

/**
 * Persistent overview map — keep this mounted at all times (use CSS display to show/hide).
 * Markers are updated whenever `organisations` or `onSelectOrganisation` changes.
 *
 * @prop {number|string} [centerOrgId] — when set, the map pans & zooms to that org's pin
 * @prop {string}       [height]      — CSS height for the outer box (defaults to 'calc(100vh - 180px)')
 */
function OrganisationsMap({ organisations, onSelectOrganisation, centerOrgId, height }) {
  const { isLoaded } = useGoogleMaps();
  const mapRef       = useRef(null);
  const markersRef   = useRef([]);
  const infoWindowRef = useRef(null);
  const [mapReady, setMapReady]   = useState(false);
  const onSelectRef  = useRef(onSelectOrganisation);

  // Keep callback ref up to date without triggering marker rebuild
  useEffect(() => { onSelectRef.current = onSelectOrganisation; }, [onSelectOrganisation]);

  const mappable = (organisations || []).filter(
    (o) => o.latitude != null && o.longitude != null
  );

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  // Rebuild markers whenever the set of organisations changes
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

    // Clear old markers and the shared InfoWindow
    markersRef.current.forEach((m) => { m.map = null; });
    markersRef.current = [];
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }

    // One shared InfoWindow for hover previews
    const infoWindow = new window.google.maps.InfoWindow({ disableAutoPan: true });
    infoWindowRef.current = infoWindow;

    mappable.forEach((org) => {
      const isSchool   = org.isSchool   === 1 || org.isSchool   === true;
      const isBusiness = org.isBusiness === 1 || org.isBusiness === true;

      let bgColor = COLOR_BUSINESS;
      if (isSchool && !isBusiness)     bgColor = COLOR_SCHOOL;
      else if (isSchool && isBusiness) bgColor = COLOR_BOTH;

      const pin = new window.google.maps.marker.PinElement({
        background:  bgColor,
        borderColor: '#fff',
        glyphColor:  '#fff',
        scale: 1.1,
      });

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map:      mapRef.current,
        position: { lat: Number(org.latitude), lng: Number(org.longitude) },
        title:    org.name,
        content:  pin,
      });

      // Click → open organisation details
      const handleClick = () => {
        infoWindow.close();
        onSelectRef.current?.(org);
      };
      marker.addEventListener('gmp-click', handleClick);
      // Fallback: also attach to the underlying DOM element
      if (pin.element) pin.element.addEventListener('click', handleClick);

      // Hover → show info window
      marker.addEventListener('gmp-mouseenter', () => {
        infoWindow.setContent(buildInfoWindowContent(org));
        infoWindow.open({ map: mapRef.current, anchor: marker });
      });
      marker.addEventListener('gmp-mouseleave', () => {
        infoWindow.close();
      });

      markersRef.current.push(marker);
    });

    if (mappable.length > 0 && !centerOrgId) {
      const bounds = new window.google.maps.LatLngBounds();
      mappable.forEach((o) => bounds.extend({ lat: Number(o.latitude), lng: Number(o.longitude) }));
      mapRef.current.fitBounds(bounds, { top: 60, right: 40, bottom: 60, left: 40 });
    }
  // Stringify ids so effect fires when the actual org set changes, not on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, JSON.stringify(mappable.map(o => o.id))]);

  // Pan & zoom to the selected org whenever centerOrgId changes
  useEffect(() => {
    if (!mapReady || !mapRef.current || !centerOrgId) return;
    const target = mappable.find(o => String(o.id) === String(centerOrgId));
    if (!target) return;
    mapRef.current.panTo({ lat: Number(target.latitude), lng: Number(target.longitude) });
    mapRef.current.setZoom(14);
    // Pop open the info window for the centred org
    if (infoWindowRef.current) {
      infoWindowRef.current.setContent(buildInfoWindowContent(target));
      const targetMarker = markersRef.current.find(m => m.title === target.name);
      if (targetMarker) {
        infoWindowRef.current.open({ map: mapRef.current, anchor: targetMarker });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, centerOrgId]);

  if (!isLoaded) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">Loading map…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: height || 'calc(100vh - 180px)', minHeight: 480 }}>
      <GoogleMap
        mapContainerStyle={MAP_STYLE}
        center={AU_CENTER}
        zoom={5}
        options={MAP_OPTIONS}
        onLoad={onMapLoad}
      />

      {/* Legend */}
      <Box sx={{
        position: 'absolute', bottom: 32, left: 12,
        bgcolor: 'rgba(255,255,255,0.93)', borderRadius: 2,
        px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.5,
        boxShadow: 2,
      }}>
        {[
          { color: COLOR_SCHOOL,   label: 'School' },
          { color: COLOR_BUSINESS, label: 'Business' },
          { color: COLOR_BOTH,     label: 'School & Business' },
        ].map(({ color, label }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
        {mappable.length < (organisations || []).length && (
          <Typography variant="caption" color="text.secondary"
            sx={{ mt: 0.5, borderTop: '1px solid #eee', pt: 0.5 }}>
            {(organisations || []).length - mappable.length} without coordinates hidden
          </Typography>
        )}
      </Box>

      {/* Hint */}
      <Box sx={{
        position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
        bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 2,
        px: 2, py: 0.75, boxShadow: 1, pointerEvents: 'none', whiteSpace: 'nowrap',
      }}>
        <Typography variant="caption" color="text.secondary">
          Click a pin to view organisation details
        </Typography>
      </Box>
    </Box>
  );
}

export default OrganisationsMap;
